import React from "react";

import { resolveSlot } from "../../utils/adConfig";
import { AdfoxAdapter } from "./AdfoxAdapter";
import { YandexAdapter } from "./YandexAdapter";
import SmiWidget from "../smiWidgets/SmiWidget";
import MoeVideo from "../smiWidgets/MoeVideo";

/**
 * Единая точка входа рендера рекламного слота.
 *
 * Вызов знает только имя слота: провайдер, вариант и участие в ротации уже
 * решены — провайдер полем в БД, вариант резолвером, плечо ротации сервером.
 *
 * `render` нужен слотам, у которых компонент шаблона не сводится к адаптеру
 * провайдера: MoeVideo в разных шаблонах разный и принимает свои пропы. Тогда слот
 * по-прежнему решает, брать запись из БД или уйти в бандл, а рисует её шаблон сам.
 *
 * Пока кампания не перенесена в БД (или выключена флагом `is_active`), слот
 * рендерит `fallback` — существующий JSX шаблона. Так поведение при пустой БД
 * остаётся ровно текущим, включая семантику фолбэка на `main` у каждого шаблона.
 */
export function AdSlot({ slot, isFull, isDesktop, fallback = null, className, render = null }) {
    const entries = resolveSlot(slot, { isFull, isDesktop });

    // Ровно null — слота в кампании нет, рисуем прежний JSX шаблона. Пустой массив
    // означает другое: слот заведён, но плечо ротации проиграло, и уходить в fallback
    // нельзя — иначе рядом с выбранным плечом появится второе.
    if (entries === null) {
        return fallback;
    }

    return (
        <React.Fragment>
            {entries.map((entry, index) =>
                render ? (
                    <React.Fragment key={`${slot}-${index}`}>{render(entry)}</React.Fragment>
                ) : (
                    <AdEntry
                        key={`${slot}-${index}`}
                        entry={entry}
                        isFull={isFull}
                        isDesktop={isDesktop}
                        className={className}
                    />
                ),
            )}
        </React.Fragment>
    );
}

function AdEntry({ entry, isFull, isDesktop, className }) {
    if (entry.provider === "adfox") {
        return <AdfoxAdapter entry={entry} className={className} />;
    }

    if (entry.provider === "yandex") {
        return <YandexAdapter entry={entry} />;
    }

    if (entry.provider === "moevideo") {
        return <MoeVideo placements={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
    }

    return <SmiWidget blockIds={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
}

export default AdSlot;
